import { inject, Injectable, OnDestroy } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { environment } from '../../../../environments/environment'
import {
  Subject,
  Subscription,
  timer,
  switchMap,
  catchError,
  of,
  tap,
  retry,
  EMPTY,
} from 'rxjs'

/**
 * Représente un changement du vivier d'optimisation.
 */
export interface PoolChange {
  id: number
  action:
    | 'affair_added'
    | 'affair_updated'
    | 'affair_removed'
    | 'affair_reactivated'
    | 'optimization_completed'
    | 'slots_recalculated'
    | 'affair_optimizing'
    | 'affair_snoozed'
    | 'affair_unsnoozed'
    | 'tours_recalculating'
  affairId: number | null
  runId: number | null
  source: string | null
  metadata: Record<string, any> | null
  affectedSlots?: Record<string, any[]> | null
  createdAt: string
}

/** Paire (date, technicien) impactée par un recalcul. */
export interface RecalculatingPair {
  date: string
  technician_id: number
}

interface PoolChangesResponse {
  changes: PoolChange[]
  serverTime: string
}

/**
 * Service de polling temps réel pour le vivier d'optimisation.
 *
 * Fonctionnement :
 * - Poll le backend toutes les POLL_INTERVAL_MS millisecondes
 * - Émet les changements détectés via les Subjects typés
 * - Gère la reconnexion automatique avec backoff exponentiel en cas d'erreur
 * - Garde un lastServerTime pour ne récupérer que les changements récents
 * - S'arrête automatiquement quand plus aucun abonné
 */
@Injectable({ providedIn: 'root' })
export class PoolRealtimeService implements OnDestroy {
  private readonly http = inject(HttpClient)
  private readonly planifyApiUrl = environment.planifyApiUrl

  /** Intervalle de polling nominal (2 secondes) */
  private readonly POLL_INTERVAL_MS = 2_000
  /** Intervalle de polling en mode dégradé après erreur */
  private readonly ERROR_POLL_INTERVAL_MS = 15_000
  /** Intervalle du full reload de sécurité (120 secondes) */
  private readonly SAFETY_RELOAD_INTERVAL_MS = 120_000
  /** Nombre max d'erreurs consécutives avant passage en mode dégradé */
  private readonly MAX_CONSECUTIVE_ERRORS = 3

  // --- Subjects publics ---

  /** Émis quand une affaire est retirée du vivier */
  readonly affairRemoved$ = new Subject<PoolChange>()
  /** Émis quand une affaire est ajoutée au vivier */
  readonly affairAdded$ = new Subject<PoolChange>()
  /**
   * Émis quand les données d'une affaire du vivier sont modifiées
   * (ex: changement d'adresse EROS → nouvelles coordonnées, créneaux recalculés).
   */
  readonly affairUpdated$ = new Subject<PoolChange>()
  /** Émis quand une affaire est réactivée dans le vivier */
  readonly affairReactivated$ = new Subject<PoolChange>()
  /** Émis quand une optimisation est terminée */
  readonly optimizationCompleted$ = new Subject<PoolChange>()
  /** Émis quand des slots ont été recalculés */
  readonly slotsRecalculated$ = new Subject<PoolChange>()
  /** Émis quand une affaire est en cours d'optimisation (feedback progressif) */
  readonly affairOptimizing$ = new Subject<PoolChange>()
  /** Émis quand une affaire est mise en pause (snooze 1 jour) */
  readonly affairSnoozed$ = new Subject<PoolChange>()
  /** Émis quand une affaire en pause est réintroduite (snooze annulé ou expiré) */
  readonly affairUnsnoozed$ = new Subject<PoolChange>()
  /**
   * Émis quand un recalcul VROOM démarre sur une liste de paires (date, technicien).
   * Le front affiche un spinner sur chaque tournée ciblée ; il est éteint à la réception
   * du `slots_recalculated` correspondant (metadata.recalculated_pairs).
   */
  readonly toursRecalculating$ = new Subject<PoolChange>()
  /** Émis quand n'importe quel changement survient (agrégé) */
  readonly anyChange$ = new Subject<PoolChange[]>()
  /** Demande un full reload du composant (fallback sécurité) */
  readonly requestFullReload$ = new Subject<void>()
  /** Statut de la connexion */
  readonly connected$ = new Subject<boolean>()

  // --- État interne ---

  private pollSubscription: Subscription | null = null
  private safetySubscription: Subscription | null = null
  private lastServerTime: string | null = null
  private subscriberCount = 0
  private consecutiveErrors = 0
  private currentInterval = this.POLL_INTERVAL_MS
  private lastProcessedChangeId = 0

  /**
   * Enregistre un abonné. Le polling démarre automatiquement au premier abonné.
   * Retourne une fonction de désinscription à appeler dans ngOnDestroy.
   */
  subscribe(): () => void {
    this.subscriberCount++

    if (this.subscriberCount === 1) {
      this.startPolling()
    }

    return () => {
      this.subscriberCount = Math.max(0, this.subscriberCount - 1)
      if (this.subscriberCount === 0) {
        this.stopPolling()
      }
    }
  }

  ngOnDestroy(): void {
    this.stopPolling()
  }

  /**
   * Force un full reload immédiat (utile après une action manuelle).
   */
  triggerFullReload(): void {
    this.requestFullReload$.next()
  }

  /**
   * Réinitialise le curseur de temps (utile après un full reload réussi).
   */
  resetCursor(): void {
    this.lastServerTime = new Date().toISOString()
    this.lastProcessedChangeId = 0
  }

  // --- Polling interne ---

  private startPolling(): void {
    if (this.pollSubscription) return

    // Initialiser le curseur à maintenant
    if (!this.lastServerTime) {
      this.lastServerTime = new Date().toISOString()
    }

    this.pollSubscription = timer(0, this.currentInterval)
      .pipe(
        switchMap(() => this.fetchChanges()),
        tap((response) => this.handleResponse(response)),
        catchError((err) => {
          this.handleError(err)
          return EMPTY
        }),
        retry({ delay: () => timer(this.currentInterval) })
      )
      .subscribe()

    // Filet de sécurité : full reload périodique
    this.safetySubscription = timer(
      this.SAFETY_RELOAD_INTERVAL_MS,
      this.SAFETY_RELOAD_INTERVAL_MS
    ).subscribe(() => {
      this.requestFullReload$.next()
    })

    this.connected$.next(true)
  }

  private stopPolling(): void {
    this.pollSubscription?.unsubscribe()
    this.pollSubscription = null
    this.safetySubscription?.unsubscribe()
    this.safetySubscription = null
    this.connected$.next(false)
  }

  private fetchChanges() {
    const params: Record<string, string> = {}
    if (this.lastServerTime) {
      params['since'] = this.lastServerTime
    }

    return this.http.get<PoolChangesResponse>(
      `${this.planifyApiUrl}/affairs/optimization/pool/changes`,
      { params }
    )
  }

  private handleResponse(response: PoolChangesResponse): void {
    // Reset error count on success
    if (this.consecutiveErrors > 0) {
      this.consecutiveErrors = 0
      this.restartWithInterval(this.POLL_INTERVAL_MS)
      this.connected$.next(true)
    }

    // Mettre à jour le curseur avec l'heure serveur
    if (response.serverTime) {
      this.lastServerTime = response.serverTime
    }

    if (!response.changes || response.changes.length === 0) {
      return
    }

    // Filtrer les doublons (protection idempotence)
    const newChanges = response.changes.filter(
      (c) => c.id > this.lastProcessedChangeId
    )

    if (newChanges.length === 0) return

    // Mettre à jour le dernier ID traité
    this.lastProcessedChangeId = Math.max(...newChanges.map((c) => c.id))

    // Dispatcher chaque changement vers le bon Subject
    for (const change of newChanges) {
      switch (change.action) {
        case 'affair_removed':
          this.affairRemoved$.next(change)
          break
        case 'affair_added':
          this.affairAdded$.next(change)
          break
        case 'affair_updated':
          this.affairUpdated$.next(change)
          break
        case 'affair_reactivated':
          this.affairReactivated$.next(change)
          break
        case 'optimization_completed':
          this.optimizationCompleted$.next(change)
          break
        case 'slots_recalculated':
          this.slotsRecalculated$.next(change)
          break
        case 'affair_optimizing':
          this.affairOptimizing$.next(change)
          break
        case 'affair_snoozed':
          // Un snooze se comporte comme un retrait du vivier côté affichage.
          this.affairSnoozed$.next(change)
          this.affairRemoved$.next(change)
          break
        case 'affair_unsnoozed':
          this.affairUnsnoozed$.next(change)
          break
        case 'tours_recalculating':
          this.toursRecalculating$.next(change)
          break
      }
    }

    // Émettre le batch complet
    this.anyChange$.next(newChanges)

    // Slots recalculés AVEC delta → pas besoin de full reload (le component fait un patch local)
    // Slots recalculés SANS delta ou optimisation complète → full reload
    const hasStructuralChange = newChanges.some(
      (c) =>
        c.action === 'optimization_completed' ||
        (c.action === 'slots_recalculated' && !c.affectedSlots)
    )
    if (hasStructuralChange) {
      this.requestFullReload$.next()
    }
  }

  private handleError(err: any): void {
    this.consecutiveErrors++
    console.warn(
      `[PoolRealtime] Erreur de polling (${this.consecutiveErrors}/${this.MAX_CONSECUTIVE_ERRORS})`,
      err
    )

    if (this.consecutiveErrors >= this.MAX_CONSECUTIVE_ERRORS) {
      this.connected$.next(false)
      this.restartWithInterval(this.ERROR_POLL_INTERVAL_MS)
    }
  }

  private restartWithInterval(interval: number): void {
    if (this.currentInterval === interval) return
    this.currentInterval = interval
    // Redémarrer le polling avec le nouvel intervalle
    this.stopPolling()
    if (this.subscriberCount > 0) {
      this.startPolling()
    }
  }
}
